import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { 
  UPLOAD_EXCEL_DATA, 
  GET_EXCEL_UPLOAD_HISTORIES, 
  GET_EXCEL_UPLOAD_ERRORS,
  GET_EXCEL_TEMPLATE_INFO 
} from '../graphql-queries/common/excelQueries';
import Message from './message/Message';

/**
 * 파일을 Base64로 인코딩하는 함수
 * @param {File} file - 변환할 파일
 * @returns {Promise<string>} Base64 인코딩된 문자열
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // data:application/... 부분을 제거하고 Base64 데이터만 추출
      const base64Data = reader.result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * 엑셀 업로드 훅
 * @returns {Object} 업로드 함수와 상태
 */
export const useExcelUpload = () => {
  const [uploadExcelData, { loading, error }] = useMutation(UPLOAD_EXCEL_DATA);

  const uploadExcel = async (file, targetTable, contextData = null, options = {}) => {
    try {
      // 파일을 Base64로 변환
      const fileData = await fileToBase64(file);
      
      // 기본 옵션 설정
      const uploadOptions = {
        skipEmptyRows: true,
        validateOnly: false,
        headerRowIndex: 0,
        startDataRowIndex: 1,
        maxErrorRows: 100,
        ...options
      };

      const request = {
        targetTable,
        fileData,
        fileName: file.name,
        contextData,
        options: uploadOptions
      };

      const result = await uploadExcelData({
        variables: { request }
      });

      const response = result.data.uploadExcelData;
      
      if (response.success) {
        Message.showSuccess(
          response.message || 
          `업로드 완료: 총 ${response.totalRows}개 중 ${response.successRows}개 성공`
        );
      } else {
        if (response.errors && response.errors.length > 0) {
          const firstError = response.errors[0];
          Message.showError(`${firstError.rowNum}행: ${firstError.errorMessage}`);
        } else {
          Message.showError(response.message || '업로드 중 오류가 발생했습니다.');
        }
      }

      return response;
    } catch (err) {
      console.error('엑셀 업로드 오류:', err);
      Message.showError('업로드 중 오류가 발생했습니다.');
      throw err;
    }
  };

  return {
    uploadExcel,
    loading,
    error
  };
};

/**
 * 엑셀 업로드 이력 조회 훅
 * @param {Object} filter - 필터 조건
 * @returns {Object} 이력 데이터와 상태
 */
export const useExcelUploadHistories = (filter = {}) => {
  const { data, loading, error, refetch } = useQuery(GET_EXCEL_UPLOAD_HISTORIES, {
    variables: { filter },
    fetchPolicy: 'cache-and-network'
  });

  return {
    histories: data?.getExcelUploadHistories || [],
    loading,
    error,
    refetch
  };
};

/**
 * 특정 업로드의 에러 로그 조회 훅
 * @returns {Object} 에러 조회 함수와 상태
 */
export const useExcelUploadErrors = () => {
  const [getErrors, { data, loading, error }] = useLazyQuery(GET_EXCEL_UPLOAD_ERRORS);

  const fetchErrors = (uploadId) => {
    return getErrors({
      variables: { uploadId }
    });
  };

  return {
    errors: data?.getExcelUploadErrors || [],
    fetchErrors,
    loading,
    error
  };
};

/**
 * 엑셀 템플릿 정보 조회 훅
 * @param {string} targetTable - 대상 테이블
 * @returns {Object} 템플릿 정보와 상태
 */
export const useExcelTemplateInfo = (targetTable) => {
  const { data, loading, error, refetch } = useQuery(GET_EXCEL_TEMPLATE_INFO, {
    variables: { targetTable },
    skip: !targetTable,
    fetchPolicy: 'cache-and-network'
  });

  return {
    templateInfo: data?.getExcelTemplateInfo,
    loading,
    error,
    refetch
  };
};

/**
 * 엑셀 업로드 에러 상세 정보 포맷팅
 * @param {Array} errors - 에러 배열
 * @returns {string} 포맷된 에러 메시지
 */
export const formatUploadErrors = (errors) => {
  if (!errors || errors.length === 0) {
    return '';
  }

  const errorMessages = errors.slice(0, 5).map(error => 
    `${error.rowNum}행 ${error.columnName}: ${error.errorMessage}`
  );

  let message = errorMessages.join('\n');
  
  if (errors.length > 5) {
    message += `\n... 외 ${errors.length - 5}개 에러`;
  }

  return message;
};

/**
 * 업로드 상태에 따른 색상 반환
 * @param {string} status - 업로드 상태
 * @returns {string} 색상 코드
 */
export const getStatusColor = (status) => {
  switch (status) {
    case 'SUCCESS':
      return 'success';
    case 'PARTIAL_SUCCESS':
      return 'warning';
    case 'FAILED':
      return 'error';
    case 'VALIDATION_ONLY':
      return 'info';
    default:
      return 'default';
  }
};

/**
 * 업로드 상태 한글 변환
 * @param {string} status - 업로드 상태
 * @returns {string} 한글 상태
 */
export const getStatusText = (status) => {
  switch (status) {
    case 'SUCCESS':
      return '성공';
    case 'PARTIAL_SUCCESS':
      return '부분 성공';
    case 'FAILED':
      return '실패';
    case 'VALIDATION_ONLY':
      return '검증만 수행';
    default:
      return '알 수 없음';
  }
}; 